<template>
  <div class="live-portal-frame">
    <v-alert v-if="error" type="error" outlined class="ma-4">
      {{ error }}
      <v-btn text color="primary" class="ml-2" @click="open">Try again</v-btn>
    </v-alert>
    <v-progress-linear v-else-if="!portalReady" indeterminate color="primary" aria-label="Loading live RepairNest portal" />
    <iframe
      v-if="src"
      ref="portal"
      :src="src"
      title="RepairNest back office"
      class="live-portal-frame__iframe"
      :class="{ 'live-portal-frame__iframe--ready': portalReady }"
      sandbox="allow-scripts allow-same-origin allow-forms allow-downloads allow-popups"
      @load="onLoad"
    />
  </div>
</template>

<script>
import backofficeService from "@/api/backoffice";

const portalOrigin = "https://admin.repairnestpos.com";

export default {
  name: "LivePortalFrame",
  props: { path: { type: String, required: true } },
  data: () => ({ src: "", error: "", portalReady: false, nonce: "", enteredPortal: false, helloTimer: null, loadTimer: null, logoutComplete: null }),
  mounted() {
    window.addEventListener("message", this.onMessage);
    window.addEventListener("repairnest:before-app-logout", this.beforeAppLogout);
    this.open();
  },
  beforeDestroy() {
    window.removeEventListener("message", this.onMessage);
    window.removeEventListener("repairnest:before-app-logout", this.beforeAppLogout);
    clearInterval(this.helloTimer);
    clearTimeout(this.loadTimer);
  },
  watch: {
    path(value) {
      if (this.enteredPortal && value !== "/notifications" && value.startsWith("/") && !value.startsWith("//")) {
        this.$refs.portal.contentWindow.postMessage({ type: "repairnest:navigate", nonce: this.nonce, path: value }, portalOrigin);
      }
    },
  },
  methods: {
    async open() {
      this.error = "";
      this.src = "";
      this.portalReady = false;
      this.enteredPortal = false;
      clearTimeout(this.loadTimer);
      try {
        const organizationId = localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}organizationId`);
        if (!organizationId) throw new Error("No organization selected");
        const response = await backofficeService.Api().post("mobile/handoff", { organizationId });
        const code = response.data && response.data.data && response.data.data.code;
        if (!code || !/^[A-Za-z0-9_-]{43}$/.test(code)) throw new Error("Invalid handoff response");
        this.src = `${portalOrigin}/mobile-handoff#code=${encodeURIComponent(code)}`;
        this.loadTimer = setTimeout(() => {
          if (!this.portalReady) {
            clearInterval(this.helloTimer);
            this.src = "";
            this.error = "The live portal did not respond. Check your connection and try again.";
          }
        }, 15000);
      } catch (error) {
        this.error = "The live portal could not be opened. Check your connection and try again.";
      }
    },
    onLoad() {
      if (!this.$refs.portal) return;
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      this.nonce = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
      clearInterval(this.helloTimer);
      const hello = () => this.$refs.portal && this.$refs.portal.contentWindow.postMessage({ type: "repairnest:hello", nonce: this.nonce }, portalOrigin);
      hello();
      this.helloTimer = setInterval(hello, 500);
    },
    beforeAppLogout(event) {
      if (!this.$refs.portal || !this.portalReady) { event.detail.complete(); return; }
      this.logoutComplete = event.detail.complete;
      this.$refs.portal.contentWindow.postMessage({ type: "repairnest:logout", nonce: this.nonce }, portalOrigin);
    },
    onMessage(event) {
      if (!this.$refs.portal || event.origin !== portalOrigin || event.source !== this.$refs.portal.contentWindow) return;
      const message = event.data || {};
      if (message.type === "repairnest:ready" && message.nonce === this.nonce) {
        clearInterval(this.helloTimer);
        // The child first authenticates at /mobile-handoff. Do not navigate it
        // away before the one-time exchange has completed.
        if (message.path !== "/mobile-handoff") {
          this.enteredPortal = true;
          this.portalReady = true;
          clearTimeout(this.loadTimer);
        }
      } else if (message.type === "repairnest:logged-out" && message.nonce === this.nonce) {
        if (this.logoutComplete) this.logoutComplete();
        this.logoutComplete = null;
      } else if (message.type === "repairnest:navigated" && message.nonce === this.nonce && typeof message.path === "string" && message.path.startsWith("/") && !message.path.startsWith("//")) {
        if (message.path === "/login" || message.path === "/logout") {
          this.$router.replace("/logout").catch(() => {});
          return;
        }
        if (message.path !== "/mobile-handoff") {
          this.enteredPortal = true;
          this.portalReady = true;
          clearTimeout(this.loadTimer);
          this.$emit("page-change", message.path);
        }
      }
    },
  },
};
</script>

<style scoped>
.live-portal-frame { height: 100%; min-height: 0; position: relative; }
.live-portal-frame__iframe { border: 0; height: 100%; left: 0; position: absolute; top: 0; visibility: hidden; width: 100%; }
.live-portal-frame__iframe--ready { visibility: visible; }
</style>
